import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { alpha, useTheme } from '@mui/material/styles';

import { MetricInfoHover } from './MetricInfoHover';
import { TILE_MIN_HEIGHT } from './TileSkeleton';

/**
 * MetricTile — the one reusable practice-metric dashboard card.
 *
 * Every metric (ratio / count / count-distinct / state) renders through this
 * single bespoke tile by emitting the same `MetricTileData` payload, so the
 * server stays data-driven (an agent mints a new metric by emitting data, not
 * shipping a component). The marker'd trend lives in the shared `ChartRenderer`
 * (the `chart` primitive); this tile carries the headline number, the lever,
 * the gate, and an at-a-glance sparkline.
 */

export type MetricGrade =
  | 'deterministic'
  | 'ai'
  | 'count'
  | 'distinct'
  | 'state';

export type MetricSparkPoint = { x: string; y: number };

export type MetricDelta = { dir: 'up' | 'down' | 'flat'; label: string };

export type MetricTileData = {
  /** Display name, e.g. "Current med-list". */
  name: string;
  /** Scope + shape line, e.g. "MM visits · ratio". */
  appliesTo: string;
  /** Drives the sparkline accent color; no longer rendered as a chip. */
  grade: MetricGrade;
  /** Headline value, already formatted, e.g. "51%" / "4" / "14". */
  value: string;
  /** Caption under the value, e.g. "documented". */
  valueCaption?: string;
  /** Sample-size line, e.g. "n=37 · 90d". */
  nLabel?: string;
  delta?: MetricDelta;
  /** The lever — the action that moves this metric. */
  lever?: string;
  /**
   * Mini trend; each date in `markerXs` draws an intervention line. `markerX`
   * (single) is kept for back-compat with demo/story data — when both are set,
   * `markerXs` wins (`markerX` is only the fallback).
   */
  spark?: { points: MetricSparkPoint[]; markerX?: string; markerXs?: string[] };
  /**
   * Below-sample-gate marker. Gated metrics are NOT rendered as cards — the
   * page filters them out (see `isMetricGated`) and lists their names in the
   * end-of-page ghost tile; the home strip drops them entirely.
   */
  gated?: { applicable: number; threshold: number };
  /**
   * Explainer for the "?" affordance in the tile header — what the metric
   * shows and how it's measured. Backend-supplied (help_text / help_tracked_by);
   * absent → no "?" renders.
   */
  help?: { body: string; trackedBy?: string };
};

/** A metric is gated when its applicable sample is below the gate threshold. */
export const isMetricGated = (d: MetricTileData): boolean =>
  !!d.gated && d.gated.applicable < d.gated.threshold;

type MainTone = 'primary' | 'success' | 'warning' | 'info' | 'error';

const GRADE_TONE: Record<MetricGrade, MainTone> = {
  deterministic: 'success',
  ai: 'warning',
  count: 'info',
  distinct: 'primary',
  state: 'warning',
};

const DELTA_COLOR: Record<MetricDelta['dir'], (t: Theme) => string> = {
  up: t => t.palette.success.dark,
  down: t => t.palette.error.main,
  flat: t => t.palette.text.disabled,
};

const Sparkline: React.FC<{
  points: MetricSparkPoint[];
  markers: string[];
  color: string;
  markerColor: string;
}> = ({ points, markers, color, markerColor }) => {
  const W = 260;
  const H = 54;
  const padY = 8;
  if (points.length === 0) return null;

  const times = points.map(p => new Date(p.x).getTime());
  const t0 = Math.min(...times);
  const t1 = Math.max(...times);
  const ys = points.map(p => p.y);
  const yMin = Math.min(...ys);
  const yMax = Math.max(...ys);

  const xAt = (t: number) => (t1 === t0 ? 0 : ((t - t0) / (t1 - t0)) * W);
  // Clamp into view so a marker dated after the last trend point still shows.
  const clampX = (px: number) => Math.max(0, Math.min(W, px));
  const yAt = (v: number) =>
    yMax === yMin
      ? H / 2
      : H - padY - ((v - yMin) / (yMax - yMin)) * (H - padY * 2);

  const poly = points
    .map((p, i) => `${xAt(times[i]).toFixed(1)},${yAt(p.y).toFixed(1)}`)
    .join(' ');

  const markerPxs = markers
    .map(m => new Date(m).getTime())
    .filter(t => !Number.isNaN(t))
    .map(t => clampX(xAt(t)));

  return (
    <Box
      component="svg"
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      sx={{ width: '100%', height: 54, display: 'block' }}
    >
      {markerPxs.map((px, i) => (
        <line
          key={`${px}-${i}`}
          x1={px}
          y1={4}
          x2={px}
          y2={H - 4}
          stroke={markerColor}
          strokeWidth={1.4}
          strokeDasharray="3 2"
        />
      ))}
      <polyline
        points={poly}
        fill="none"
        stroke={color}
        strokeWidth={2.4}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Box>
  );
};

export const MetricTile: React.FC<{
  data: MetricTileData;
  /**
   * When provided, the lever box becomes an actionable "Improve this ↗" CTA
   * that opens the optimization funnel. The composing page passes this only for
   * flat / underperforming metrics — a healthy, climbing metric keeps the
   * passive lever caption. SDUI dispatch omits it entirely.
   */
  onImprove?: () => void;
}> = ({ data, onImprove }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'grey.200',
        borderRadius: 2.5,
        bgcolor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        minWidth: 260,
        minHeight: TILE_MIN_HEIGHT,
        height: '100%',
        pb: 1.5,
      }}
    >
      {/* Header */}
      <Box sx={{ px: 1.875, pt: 1.625, pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 700,
              lineHeight: 1.15,
              color: 'text.primary',
            }}
          >
            {data.name}
          </Typography>
          {data.help && <MetricInfoHover name={data.name} help={data.help} />}
        </Box>
        <Typography
          sx={{
            mt: 0.4,
            fontSize: 9.5,
            fontWeight: 700,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: 'text.secondary',
          }}
        >
          {data.appliesTo}
        </Typography>

        <Stack
          direction="row"
          spacing={1}
          sx={{ mt: 1, mb: 0.25, alignItems: 'baseline' }}
        >
          <Typography
            sx={{
              fontSize: 38,
              fontWeight: 700,
              lineHeight: 0.9,
              color: 'text.primary',
            }}
          >
            {data.value}
          </Typography>
          {data.delta && (
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 700,
                color: DELTA_COLOR[data.delta.dir](theme),
              }}
            >
              {data.delta.label}
            </Typography>
          )}
        </Stack>
        {(data.valueCaption || data.nLabel) && (
          <Typography
            sx={{ fontSize: 10.5, fontWeight: 600, color: 'text.secondary' }}
          >
            {data.valueCaption}
            {data.valueCaption && data.nLabel ? ' · ' : ''}
            {data.nLabel && (
              <Box component="span" sx={{ color: 'text.primary' }}>
                {data.nLabel}
              </Box>
            )}
          </Typography>
        )}
      </Box>

      {/* Sparkline */}
      {data.spark && (
        <Box sx={{ px: 1.875, pt: 0.5, pb: 0.25 }}>
          <Sparkline
            points={data.spark.points}
            markers={
              data.spark.markerXs ??
              (data.spark.markerX ? [data.spark.markerX] : [])
            }
            color={theme.palette[GRADE_TONE[data.grade]].dark}
            markerColor={theme.palette.warning.dark}
          />
        </Box>
      )}

      {/* Lever — passive caption, or an actionable CTA on flagged metrics */}
      {data.lever &&
        (onImprove ? (
          <Box
            component="button"
            onClick={e => {
              // The tile is wrapped in a drill-in link; keep the lever click local.
              e.stopPropagation();
              e.preventDefault();
              onImprove();
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              width: 'calc(100% - 26px)',
              textAlign: 'left',
              cursor: 'pointer',
              mx: 1.625,
              mt: 0.75,
              bgcolor: alpha(theme.palette.warning.main, 0.14),
              border: '1px solid',
              borderColor: alpha(theme.palette.warning.main, 0.4),
              borderRadius: 1.75,
              px: 1.375,
              py: 0.875,
              transition: 'background-color 120ms, border-color 120ms',
              '&:hover': {
                bgcolor: alpha(theme.palette.warning.main, 0.22),
                borderColor: alpha(theme.palette.warning.main, 0.6),
              },
            }}
          >
            <Typography sx={{ fontSize: 11, color: 'text.secondary', lineHeight: 1.35, flex: 1 }}>
              <Box component="span" sx={{ fontWeight: 700, color: 'warning.dark' }}>
                Optimize:{' '}
              </Box>
              {data.lever}
            </Typography>
            <Typography
              sx={{ fontSize: 11, fontWeight: 800, color: 'warning.dark', whiteSpace: 'nowrap' }}
            >
              Improve this ↗
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              mx: 1.625,
              mt: 0.75,
              bgcolor: alpha(theme.palette.warning.main, 0.1),
              border: '1px solid',
              borderColor: alpha(theme.palette.warning.main, 0.3),
              borderRadius: 1.75,
              px: 1.375,
              py: 0.875,
            }}
          >
            <Typography sx={{ fontSize: 11, color: 'text.secondary', lineHeight: 1.35 }}>
              <Box component="span" sx={{ fontWeight: 700, color: 'warning.dark' }}>
                Optimize:{' '}
              </Box>
              {data.lever}
            </Typography>
          </Box>
        ))}
    </Box>
  );
};
